import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameWaitingState } from '../../engine/stepEngine';
import { resolveMinigameEntry, resolveRegistryEntry } from '../../config/minigameRegistry';
import { minigameComponents, minigameComponentsByTemplateId } from '../../minigames/templates';
import type { RuntimeContext } from '../../runtime/playerRuntime';
import type { Envelope, HostMessageType, MinigameMessageType } from 'shared';
import { audioEngine } from '../../audio/audioEngine';
import { readDomMood } from '../../minigames/foundation/hooks/readDomMood';
import { setActiveMinigameSe } from '../../minigames/foundation/hooks/minigameSe';
import { resolveMediaRefToUrl } from '../../utils/mediaRef';
import { minigameHostClassName } from './minigameHostClassName';

/** P8-1: config.audio の AssetRef（{assetId?, url?}）を再生可能 URL へ解決する。
 *  assetId（UUID）優先・無ければ url をそのまま使う。どちらも無ければ undefined。 */
function resolveSeUrl(ref: unknown): string | undefined {
  if (!ref || typeof ref !== 'object') return undefined;
  const r = ref as { assetId?: unknown; url?: unknown };
  if (typeof r.assetId === 'string' && r.assetId.trim()) {
    const resolved = resolveMediaRefToUrl(r.assetId.trim());
    if (resolved) return resolved;
  }
  if (typeof r.url === 'string' && r.url.trim()) return r.url.trim();
  return undefined;
}

interface MinigameHostProps {
  waiting: MinigameWaitingState;
  runtimeContext?: RuntimeContext;
  onSubmit: (result: Record<string, unknown>) => Promise<void>;
  productionMode?: boolean;
  /** 現在の背景状態（ミニゲームに引き継ぐ） */
  currentBackground?: { type: 'image' | 'color'; value: string; overlay?: string } | null;
}

interface MinigameResultPayload {
  result?: Record<string, unknown>;
  [key: string]: unknown;
}

export function MinigameHost({ waiting, runtimeContext, onSubmit, productionMode = false, currentBackground }: MinigameHostProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const readyTimerRef = useRef<number | null>(null);
  const [status, setStatus] = useState<'loading' | 'waiting' | 'ready' | 'submitting' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const entry = resolveMinigameEntry(waiting.step);
  const registryEntry = useMemo(
    () => resolveRegistryEntry(waiting.step.registryId, waiting.step.templateId),
    [waiting.step.registryId, waiting.step.templateId]
  );
  const resolvedRegistryId = waiting.step.registryId ?? registryEntry?.registryId ?? null;
  const ReactMinigame = useMemo(() => {
    if (resolvedRegistryId && minigameComponents[resolvedRegistryId]) {
      return minigameComponents[resolvedRegistryId] ?? null;
    }
    if (waiting.step.templateId && minigameComponentsByTemplateId[waiting.step.templateId]) {
      return minigameComponentsByTemplateId[waiting.step.templateId] ?? null;
    }
    return null;
  }, [resolvedRegistryId, waiting.step.templateId]);

  const expectedOrigin = useMemo(() => {
    if (!entry) {
      return null;
    }
    try {
      return new URL(entry.entryUrl).origin;
    } catch {
      return null;
    }
  }, [entry]);

  const sendHostMessage = useCallback(
    (type: HostMessageType, payload: Record<string, unknown>) => {
      const frame = iframeRef.current;
      if (!frame || !frame.contentWindow || !expectedOrigin) {
        return;
      }
      const envelope: Envelope<HostMessageType, Record<string, unknown>> = {
        v: 1,
        type,
        runId: runtimeContext?.runId ?? 'mock-run',
        stepId: waiting.step.id,
        templateId: waiting.step.templateId,
        ts: Date.now(),
        payload
      };
      frame.contentWindow.postMessage(envelope, expectedOrigin);
    },
    [expectedOrigin, runtimeContext?.runId, waiting.step.id, waiting.step.templateId]
  );

  const clearReadyTimeout = () => {
    if (readyTimerRef.current) {
      window.clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }
  };

  const scheduleReadyTimeout = () => {
    clearReadyTimeout();
    readyTimerRef.current = window.setTimeout(() => {
      setStatus('error');
      setErrorMessage('ミニゲームから READY シグナルが届きませんでした。');
    }, 8000);
  };

  // React版テンプレの場合は即ready扱い
  useEffect(() => {
    if (ReactMinigame) {
      clearReadyTimeout();
      setStatus('ready');
      setErrorMessage(null);
      setHasSubmitted(false);
    }
  }, [ReactMinigame]);

  // P8-1 継ぎ目の儀式: ミニゲーム突入の短いスティング（step ごとに 1 回）。
  // 視覚（veil / マウントアニメ）は変えず、音だけで継ぎ目を包む。StrictMode の
  // 二重 mount で二重に鳴らないよう step.id で ref ガードする。mute 中は engine が無視。
  const passageStepIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (passageStepIdRef.current === waiting.step.id) return;
    passageStepIdRef.current = waiting.step.id;
    const mood = readDomMood();
    audioEngine.playSfx('passage', { mood, volume: 0.6 });
  }, [waiting.step.id]);

  // P8-1: config.audio.successSE/failSE/tapSE（凍結スキーマの既存フィールド）を
  // semantic 層（JudgementModal/FailOverlay/AdvanceTrigger）が読めるよう登録する。
  // unmount で解除（Sandbox 等の未登録経路は従来の合成音のまま）。
  useEffect(() => {
    const audioCfg = (waiting.step.config as Record<string, unknown> | undefined)?.audio as
      | Record<string, unknown>
      | undefined;
    setActiveMinigameSe({
      success: resolveSeUrl(audioCfg?.successSE),
      fail: resolveSeUrl(audioCfg?.failSE),
      tap: resolveSeUrl(audioCfg?.tapSE)
    });
    return () => setActiveMinigameSe(null);
  }, [waiting.step.config]);

  const handleIframeLoad = () => {
    if (!entry) {
      return;
    }
    setStatus('waiting');
    setErrorMessage(null);
    setHasSubmitted(false);
    scheduleReadyTimeout();
    console.debug('[host] sending HOST_INIT', {
      entryUrl: entry.entryUrl,
      expectedOrigin,
      stepId: waiting.step.id,
      templateId: waiting.step.templateId
    });
    sendHostMessage('HOST_INIT', {
      config: waiting.step.config ?? {},
      difficulty: waiting.step.difficulty,
      timeLimitMs: waiting.step.timeLimitMs,
      maxMistakes: waiting.step.maxMistakes,
      ui: waiting.step.ui,
      resultVarKey: waiting.step.resultVarKey,
      context: {
        runId: runtimeContext?.runId,
        deviceKey: runtimeContext?.deviceKey
      }
    });
    // P8-1: グローバルミュート/音量の現在値を iframe へ通知する（HOST_SET_SOUND は
    // 凍結プロトコルに元から存在する type・payload は {muted, volume} の additive 契約）。
    sendHostMessage('HOST_SET_SOUND', {
      muted: audioEngine.isMuted(),
      volume: audioEngine.getMasterVolume()
    });
  };

  // P8-1: マウント中のミュート/音量変更にも追従して HOST_SET_SOUND を送る（iframe 経路のみ）。
  useEffect(() => {
    if (ReactMinigame) return;
    const unsubscribe = audioEngine.onChange(({ muted, volume }) => {
      sendHostMessage('HOST_SET_SOUND', { muted, volume });
    });
    return unsubscribe;
  }, [ReactMinigame, sendHostMessage]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // デバッグ用に全メッセージを一旦記録（本番でも少量なので許容）
      console.debug('[host] message received', {
        origin: event.origin,
        fromIframe: event.source === iframeRef.current?.contentWindow,
        data: event.data
      });
      if (ReactMinigame) {
        return; // Reactレンダリング時はpostMessageを無視
      }
      if (!expectedOrigin || event.origin !== expectedOrigin) {
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      const envelope = event.data as Envelope<MinigameMessageType, unknown>;
      if (!isEnvelope(envelope)) {
        return;
      }

      switch (envelope.type) {
        case 'MINIGAME_READY': {
          clearReadyTimeout();
          setStatus('ready');
          setErrorMessage(null);
          break;
        }
        case 'MINIGAME_LOG': {
          console.debug('[minigame]', waiting.step.id, envelope.payload);
          break;
        }
        case 'MINIGAME_ERROR': {
          clearReadyTimeout();
          setStatus('error');
          setErrorMessage(String((envelope.payload as Record<string, unknown>)?.message ?? 'ミニゲームエラー'));
          break;
        }
        case 'MINIGAME_RESULT': {
          if (hasSubmitted) {
            return;
          }
          clearReadyTimeout();
          setStatus('submitting');
          const payload = envelope.payload as MinigameResultPayload;
          const result = payload?.result ?? payload;
          setHasSubmitted(true);
          void onSubmit(normalizeMinigameResult(result ?? {}))
            .then(() => {
              setStatus('ready');
              // P8-1: 結果受領＝シェルへの復帰リリース音（判定音はミニゲーム側の責務・これは継ぎ目の音）。
              audioEngine.playSfx('settle', { mood: readDomMood(), volume: 0.6 });
            })
            .catch((error) => {
              console.error('Failed to submit minigame result', error);
              setStatus('error');
              setErrorMessage(error instanceof Error ? error.message : '結果送信に失敗しました');
              setHasSubmitted(false);
            });
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      clearReadyTimeout();
    };
  }, [ReactMinigame, expectedOrigin, hasSubmitted, onSubmit, waiting.step.id]);

  const handleReload = () => {
    setIframeKey((prev) => prev + 1);
    setStatus('loading');
    setErrorMessage(null);
  };

  const handleReactSubmit = useCallback(
    async (result: Record<string, unknown>) => {
      setStatus('submitting');
      try {
        await onSubmit(normalizeMinigameResult(result));
        setStatus('ready');
        // P8-1: 結果受領＝シェルへの復帰リリース音（iframe 経路と対称）。
        audioEngine.playSfx('settle', { mood: readDomMood(), volume: 0.6 });
      } catch (error) {
        console.error('Failed to submit minigame result (react)', error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : '結果送信に失敗しました');
      }
    },
    [onSubmit]
  );

  // step 側のプレイ条件メタ情報。iframe へは HOST_INIT payload で渡している情報と
  // 同じ情報源（waiting.step）を React 直レンダリングのテンプレにも揃えて渡す。
  // テンプレ側での消費は R1 で実装する（現時点では未読でも無害）。
  const hostMeta = useMemo(
    () => ({
      difficulty: waiting.step.difficulty,
      timeLimitMs: waiting.step.timeLimitMs,
      maxMistakes: waiting.step.maxMistakes,
      ui: waiting.step.ui
    }),
    [waiting.step.difficulty, waiting.step.timeLimitMs, waiting.step.maxMistakes, waiting.step.ui]
  );

  // hostConfigに現在の背景を追加
  // （React の hooks ルール上、早期 return より前で呼ぶ必要があるためここで計算する）
  const enrichedConfig = useMemo(
    () => ({
      ...(waiting.step.config ?? {}),
      // 明示的なbackground設定がない場合は現在の背景を使用
      background: (waiting.step.config as Record<string, unknown>)?.background ?? currentBackground
    }),
    [waiting.step.config, currentBackground]
  );

  if (!entry && !ReactMinigame) {
    return (
      <div className="minigame-host minigame-host--error">
        <p>registryId `{waiting.step.registryId ?? 'unknown'}` のエントリが見つかりません。</p>
        <button className="secondary-btn" onClick={handleReload}>
          リロード
        </button>
      </div>
    );
  }

  if (!runtimeContext?.runId) {
    return (
      <div className="minigame-host minigame-host--error">
        <p>Run情報が無いためミニゲームを起動できません。</p>
      </div>
    );
  }

  // Reactテンプレを優先表示
  if (ReactMinigame) {
    return (
      <div className={minigameHostClassName(productionMode)}>
        <div className="minigame-react-shell">
          <div className="minigame-stage">
            <div className="minigame-stage__frame">
              <div className="minigame-stage__content">
                <ReactMinigame
                  hostSubmit={handleReactSubmit}
                  hostStatus={status}
                  hostConfig={enrichedConfig}
                  hostMeta={hostMeta}
                  registryId={resolvedRegistryId ?? undefined}
                  templateId={waiting.step.templateId}
                />
              </div>
            </div>
          </div>
        </div>
        {/* 本番モードではデバッグUIを非表示 */}
        {!productionMode && (
          <div className="minigame-host__status">
            <span className={`minigame-host__badge status-${status}`}>{statusLabel(status)}</span>
            {errorMessage && <p className="error-text">{errorMessage}</p>}
            <div className="minigame-host__actions">
              <button className="ghost-btn" disabled={status === 'submitting'} onClick={handleReload}>
                リロード
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // TypeScript: entryは上記のガードで非nullが保証されている
  if (!entry) {
    return null;
  }

  return (
    <div className={minigameHostClassName(productionMode)}>
      <div className="minigame-stage">
        <div className="minigame-stage__frame">
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={entry.entryUrl}
            title={entry.registry?.name ?? waiting.step.title ?? '調査'}
            sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-forms"
            allow="fullscreen; autoplay"
            onLoad={handleIframeLoad}
          />
        </div>
      </div>

      {/* 本番モードではデバッグUIを非表示 */}
      {!productionMode && (
        <div className="minigame-host__status">
          <span className={`minigame-host__badge status-${status}`}>{statusLabel(status)}</span>
          {errorMessage && <p className="error-text">{errorMessage}</p>}
          <div className="minigame-host__actions">
            <button className="ghost-btn" onClick={() => sendHostMessage('HOST_PAUSE', {})}>
              一時停止
            </button>
            <button className="ghost-btn" onClick={() => sendHostMessage('HOST_RESUME', {})}>
              再開
            </button>
            <button className="ghost-btn" onClick={handleReload}>
              リロード
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * onSubmit へ渡す直前の result 正規化。
 * React テンプレ（hostSubmit→handleReactSubmit）と iframe（MINIGAME_RESULT）の
 * 両経路がここを通る（合流点は onSubmit 呼び出し直前の2箇所）。
 *
 * 現行テンプレは `status: 'success' | 'fail' | ...`（文字列）を送る一方、
 * Worker の完了書計算（certificate.ts）は `success === true`（boolean）と
 * `retryCount`（number）を読むため、欠けている場合のみ補完する。
 * 既存フィールドの削除・上書きは一切行わない（追加のみ・入力は変異させずコピー）。
 *
 * R3: さらに `perfect`（ノーミスクリア）を補完する。19本のテンプレを無改修で
 * 「完璧クリア」概念に対応させ、物語側（stats / choice.condition）が参照できるようにする。
 * 既に result.perfect があればそれを尊重し上書きしない。
 */
function normalizeMinigameResult(result: Record<string, unknown>): Record<string, unknown> {
  if (!result || typeof result !== 'object') {
    return result;
  }
  const normalized: Record<string, unknown> = { ...result };
  if (!('success' in normalized)) {
    normalized.success = normalized.status === 'success';
  }
  if (!('retryCount' in normalized)) {
    normalized.retryCount = 0;
  }
  // perfect: 成功 かつ 再挑戦0 かつ ミス0（mistakes が数値でない＝計測無し型は「ミス無し」扱い）。
  // success/retryCount は上で正規化済みの値を使う。明示指定があれば尊重。
  if (!('perfect' in normalized)) {
    const noMistakes = typeof normalized.mistakes !== 'number' || normalized.mistakes === 0;
    normalized.perfect =
      normalized.success === true && normalized.retryCount === 0 && noMistakes;
  }
  return normalized;
}

function isEnvelope(value: unknown): value is Envelope<MinigameMessageType, unknown> {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  return record.v === 1 && typeof record.type === 'string' && typeof record.stepId === 'string';
}

function statusLabel(status: 'loading' | 'waiting' | 'ready' | 'submitting' | 'error'): string {
  switch (status) {
    case 'loading':
      return '読み込み中';
    case 'waiting':
      return 'READY待ち';
    case 'ready':
      return 'プレイ可能';
    case 'submitting':
      return '結果送信中';
    case 'error':
      return 'エラー';
    default:
      return status;
  }
}

